import { AxiosError } from 'axios';

/** Normalised API error. Every failed request rejects with exactly this shape. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Laravel validation errors: field -> messages (422 only). Nested keys look like `items.0.qty`. */
  readonly errors: Record<string, string[]>;
  /** Response headers worth reading (Retry-After for 429). */
  readonly retryAfter: number | null;
  /** The full parsed JSON error body (extra fields such as `product_id`, `sale_id`, `max`). Empty object when none. */
  readonly data: Record<string, unknown>;

  constructor(init: {
    message: string;
    status: number;
    code: string;
    errors?: Record<string, string[]>;
    retryAfter?: number | null;
    data?: Record<string, unknown>;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.errors = init.errors ?? {};
    this.retryAfter = init.retryAfter ?? null;
    this.data = init.data ?? {};
  }

  /** A numeric extra field from the error body (`product_id`, `sale_id`, `max`...), or null. */
  num(key: string): number | null {
    const v = this.data[key];
    const n =
      typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  }

  /** A string extra field from the error body, or null. */
  str(key: string): string | null {
    const v = this.data[key];
    return typeof v === 'string' ? v : null;
  }

  get isValidation(): boolean {
    return this.status === 422;
  }
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

const DEFAULT_MESSAGES: Record<number, string> = {
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to do that.',
  404: 'The requested item was not found.',
  405: 'That action is not allowed.',
  409: 'That action conflicts with the current data.',
  422: 'Please check the highlighted fields.',
  423: 'The system is locked.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on the server. Please try again.',
};

function defaultCode(status: number): string {
  switch (status) {
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 405:
      return 'METHOD_NOT_ALLOWED';
    case 409:
      return 'CONFLICT';
    case 422:
      return 'VALIDATION_ERROR';
    case 423:
      return 'SYSTEM_LOCKED';
    case 429:
      return 'TOO_MANY_REQUESTS';
    default:
      return status >= 500 ? 'SERVER_ERROR' : 'HTTP_ERROR';
  }
}

function isBinary(v: unknown): boolean {
  return (
    v instanceof ArrayBuffer ||
    ArrayBuffer.isView(v) ||
    (typeof Blob !== 'undefined' && v instanceof Blob)
  );
}

/**
 * Requests made with `responseType: 'arraybuffer' | 'blob'` receive their JSON error body as binary. Decode it in
 * place (on the axios error) so `normalizeError` can read message/code/extra fields. Never throws.
 */
export async function decodeBinaryErrorBody(error: unknown): Promise<void> {
  if (!(error instanceof AxiosError) || !error.response) return;
  const res = error.response;
  const raw: unknown = res.data;
  if (!isBinary(raw)) return;
  try {
    let text: string;
    if (typeof Blob !== 'undefined' && raw instanceof Blob) text = await raw.text();
    else if (raw instanceof ArrayBuffer) text = new TextDecoder().decode(raw);
    else text = new TextDecoder().decode(raw as ArrayBufferView);
    const json: unknown = JSON.parse(text);
    res.data = json && typeof json === 'object' ? json : {};
  } catch {
    res.data = {};
  }
}

/** Convert anything thrown by axios into an ApiError. Never throws. */
export function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (error instanceof AxiosError) {
    if (error.code === 'ERR_CANCELED') {
      return new ApiError({ message: 'Request cancelled.', status: 0, code: 'CANCELED' });
    }
    const res = error.response;
    if (!res) {
      const timeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
      return new ApiError({
        message: timeout
          ? 'The server took too long to respond. Please try again.'
          : 'Cannot reach the server. Check your connection and try again.',
        status: 0,
        code: timeout ? 'TIMEOUT' : 'NETWORK_ERROR',
      });
    }
    const raw: unknown = res.data;
    const parsed: Record<string, unknown> =
      raw && typeof raw === 'object' && !Array.isArray(raw) && !isBinary(raw)
        ? (raw as Record<string, unknown>)
        : {};
    const body = parsed as { message?: unknown; code?: unknown; errors?: unknown };
    const status = res.status;
    const message =
      typeof body.message === 'string' && body.message.trim() !== ''
        ? body.message
        : (DEFAULT_MESSAGES[status] ?? 'Request failed.');
    const code = typeof body.code === 'string' && body.code ? body.code : defaultCode(status);
    const errors: Record<string, string[]> = {};
    if (body.errors && typeof body.errors === 'object') {
      for (const [k, v] of Object.entries(body.errors as Record<string, unknown>)) {
        errors[k] = Array.isArray(v) ? v.map(String) : [String(v)];
      }
    }
    const ra = Number(res.headers?.['retry-after']);
    return new ApiError({
      message,
      status,
      code,
      errors,
      retryAfter: Number.isFinite(ra) && ra > 0 ? ra : null,
      data: parsed,
    });
  }

  const message = error instanceof Error ? error.message : 'Unexpected error.';
  return new ApiError({ message, status: 0, code: 'UNKNOWN' });
}

/** Message safe to show a user, for any thrown value. */
export function getErrorMessage(error: unknown): string {
  return normalizeError(error).message;
}
